/** All copy for the /vision page lives here so edits happen in one place,
    mirroring the platform page's constants pattern. The page reads as a blog
    post: problem prose with the gravity figure as its exhibit, then solution
    prose with the record figure as the payoff. Nothing is interactive; the
    figures are alive but the scroll is the only control. No section
    headings. */

export const VISION_META_DATE = "11 June 2026";

/** The bold first line of the post. Body-sized, not a display headline. */
export const VISION_CLAIM = "People got faster. Companies didn't.";

/** One orienting line above the gravity figure: the avatars make the
    people legible on their own, so the caption only decodes the dots. */
export const FIELD_CAPTION = "Every dot is a decision. The pale dots happened off the record.";

/** The mirror line above the record figure, completing the rhyme the
    gravity caption started. The people never move between the two figures;
    this line makes that the point. */
export const FIELD_RECORD_CAPTION = "Same people, same org chart. Only the decisions moved.";

/** The two figure states. Lives here, not in the component: page.tsx is a
    server component, and values exported from a "use client" module arrive
    there as client references instead of the real object. */
export const AMBIENT_FIELD_MODE = {
  /** The at-scale company under drag: alive, crowded, going nowhere */
  GRAVITY: "gravity",
  /** The same company settling onto the record, once, as it enters view */
  RECORD: "record",
} as const;

export type AmbientFieldMode = (typeof AMBIENT_FIELD_MODE)[keyof typeof AMBIENT_FIELD_MODE];

export const FIELD_GRAVITY_ARIA_LABEL =
  "An organized company, people in even rows, but decisions cluster around a few leaders while pale dots drift off the record.";

export const FIELD_RECORD_ARIA_LABEL =
  "The same people in the same rows, now with every decision on the record, organized into connected layers.";

/** The problem, above the graphic. Paragraphs flow without headings. The
    bottleneck ledger renders between the OPEN and CLOSE runs, right after the
    history paragraph it illustrates. */
export const VISION_PARAGRAPHS_PROBLEM_OPEN = [
  "Over the last few years, AI started making individuals dramatically faster. Not everyone yet, and not evenly. But the people who work with it do in an afternoon what used to take a week, and there are more of them every month. Code, contracts, models, decks: all of it is speeding up.",
  "The way your company decides together has not changed at all. It still runs on meetings, memory, and whoever holds the context. A plan that takes an afternoon to draft still takes a quarter to agree on. People are getting quicker. The company is not.",
  "This has happened before. When industry made individual work fast, the new constraint was coordination: thousands of fast hands, and nothing to make the output fit together. Every era since has hit a new bottleneck and answered it with a new management invention. Each one was a real solution, and they are the reason a company of fifty thousand people functions at all.",
];

export const VISION_PARAGRAPHS_PROBLEM_CLOSE = [
  "AI just moved the bottleneck again. When execution is fast and cheap, the constraint is no longer getting work done. The constraint is deciding what to do, who makes the call, and whether people can act on it without waiting for a meeting. Management is the bottleneck now. Not because managers got worse, but because deciding stayed manual while everything around it sped up.",
  "Growth makes it worse. Growth gives every company gravity: decisions fall toward the same few people, and the bigger the company gets, the stronger the pull. Hard calls wait on the founders. Committees form around anything contested.",
  "And the decisions that can't wait get made anyway: off the record, in DMs and hallways, where nobody can learn from them. The judgment your company already paid for becomes unreachable.",
  "It looks like this.",
];

/** The bottleneck ledger: each era's binding constraint and the management
    invention that answered it. The final row is left open on purpose; the rest
    of the page answers it. */
export interface LedgerRow {
  era: string;
  bottleneck: string;
  invention: string;
  /** The unanswered present-day row, emphasized as the page's thesis. */
  isOpen?: boolean;
}

export const LEDGER_COLUMN_BOTTLENECK = "The Bottleneck";
export const LEDGER_COLUMN_INVENTION = "The Invention";

export const LEDGER_ROWS: readonly LedgerRow[] = [
  { era: "1855", bottleneck: "coordination", invention: "the org chart" },
  { era: "1911", bottleneck: "output", invention: "scientific management" },
  { era: "1954", bottleneck: "priorities", invention: "management by objectives" },
  { era: "1975", bottleneck: "alignment", invention: "the OKR" },
  { era: "Now", bottleneck: "decisions", invention: "?", isOpen: true },
];

export const VISION_BELIEFS_INTRO = "We believe a few things about fixing this.";

export const VISION_BELIEFS = [
  "Decisions are the atomic unit of a company. Not tasks, not tickets, not meetings.",
  "Structure should carry context, so people can carry judgment.",
  "Accountability is human. Every consequential decision has a name attached.",
  "A company should get smarter as it grows, not slower.",
];

/** The solution, through the compounding-graph paragraph. The record figure
    renders right after it: the same company the reader saw under gravity,
    settling onto the record. */
export const VISION_PARAGRAPHS_SOLUTION_OPEN = [
  "So we are building the missing invention. Orgtools is a founder-mode operating model: the system of record for how your company decides. Every decision goes on the record with its reasoning, its owner, and what actually happened. Each one connects to the strategy above it and the operations below it.",
  "This is not more process. Nobody fills out a decision ticket. The record is a byproduct of deciding, not paperwork after the fact. AI gathers the context, drafts the options, and keeps the record. People decide.",
  "That record compounds into a graph your company owns. People and AI both stand on it. New people inherit it on day one.",
];

/** The close, below the record figure, through to the signature. */
export const VISION_PARAGRAPHS_SOLUTION_CLOSE = [
  "Agents raise the stakes. An agent is exactly as good as the judgment you can hand it, and judgment trapped in meetings and memory cannot be handed to anyone. The companies that can teach AI how they decide will beat the companies that just buy it.",
  "We are building this inside real companies today, on real decisions, and the infrastructure stays with each company we build it in. We are not making anyone work harder or faster. We are making the same effort add up.",
];

export const SIGNATURE_NAMES = "Travis Truett and Mark Birzell";
export const SIGNATURE_ROLE = "Founders, Orgtools";
